/**
 * VeriClean Auth Context - ISOLATED from VeriTrade AuthContext
 * Manages VeriClean user session state separately
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { vericleanAuth, setVeriCleanToken, getVeriCleanToken } from './vericleanApi';

// Create context
const VeriCleanAuthContext = createContext(null);

/**
 * VeriClean Auth Provider
 */
export function VeriCleanAuthProvider({ children }) {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async () => {
    // CRITICAL: Reset account state before checking session
    setAccount(null);
    
    const token = getVeriCleanToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      // ALWAYS fetch user from backend - never trust cached data
      const data = await vericleanAuth.me();
      if (data.ok && data.account) {
        // Set account state ONLY from fresh backend response
        setAccount(data.account);
      } else {
        setVeriCleanToken(null);
        setAccount(null);
      }
    } catch (error) {
      console.log('[VeriClean] Not authenticated');
      setVeriCleanToken(null);
      setAccount(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    // CRITICAL: Reset account state before login
    setAccount(null);
    
    const data = await vericleanAuth.login(email, password);
    if (data.ok && data.token) {
      // IMPORTANT: Fetch full account data from /me endpoint to get all fields including is_vericlean_admin
      try {
        const meData = await vericleanAuth.me();
        if (meData.ok && meData.account) {
          setAccount(meData.account);
        } else {
          // Fallback to login response if /me fails
          setAccount(data.account);
        }
      } catch (e) {
        // Fallback to login response if /me fails
        setAccount(data.account);
      }
    }
    return data;
  };

  const signup = async (signupData) => {
    const data = await vericleanAuth.signup(signupData);
    return data;
  };

  const logout = async () => {
    await vericleanAuth.logout();
    setAccount(null);
  };

  const value = {
    account,
    setAccount,
    loading,
    login,
    signup,
    logout,
    isAuthenticated: !!account,
    isCleaner: account?.role === 'cleaner',
    isCustomer: account?.role === 'customer',
  };

  return (
    <VeriCleanAuthContext.Provider value={value}>
      {children}
    </VeriCleanAuthContext.Provider>
  );
}

/**
 * Hook to access VeriClean auth context
 */
export function useVeriCleanAuth() {
  const context = useContext(VeriCleanAuthContext);
  if (!context) {
    throw new Error('useVeriCleanAuth must be used within VeriCleanAuthProvider');
  }
  return context;
}

export default VeriCleanAuthContext;
